import React, { useState, useEffect } from 'react';
import { X, Key, Password, Gift, SpinnerGap, CheckCircle, Warning, Copy, SignOut, Sliders, CreditCard, Coffee, ChartBar } from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel({ onClose }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);


  // Promo code (two fixed slots)
  const [newPromo, setNewPromo] = useState('');
  const [currentPromo, setCurrentPromo] = useState('');
  const [promoMsg, setPromoMsg] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [newPromo2, setNewPromo2] = useState('');
  const [currentPromo2, setCurrentPromo2] = useState('');
  const [promoMsg2, setPromoMsg2] = useState(null);
  const [promoLoading2, setPromoLoading2] = useState(false);

  // Generated codes
  const [codes, setCodes] = useState([]);
  const [codeStats, setCodeStats] = useState({});
  const [genLoading, setGenLoading] = useState(false);
  const [codesLoading, setCodesLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  // App Settings (toggles)
  const [settings, setSettings] = useState({ stripe_enabled: true, bmc_enabled: true, analyses_enabled: true, promo_day_enabled: false });
  const [settingsLoading, setSettingsLoading] = useState({});
  const [analysisStats, setAnalysisStats] = useState({ couponDay: null, couponTotal: null, fixedDay: null, fixedTotal: null });

  // Auth header helper
  const authHeaders = () => {
    const t = localStorage.getItem('admin_token');
    return { 'Content-Type': 'application/json', ...(t ? { 'Authorization': `Bearer ${t}` } : {}) };
  };

  // Check existing session
  useEffect(() => {
    const check = async () => {
      const t = localStorage.getItem('admin_token');
      if (!t) return;
      try {
        const res = await fetch(`${API_BASE}/api/admin/me`, { headers: { 'Authorization': `Bearer ${t}` } });
        if (res.ok) {
          setAuthenticated(true);
          loadCodes();
          loadCurrentPromo();
          loadSettings();
        } else { localStorage.removeItem('admin_token'); }
      } catch (e) { /* not logged in */ }
    };
    check();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('admin_token', data.token);
        window.dispatchEvent(new Event('admin-auth-changed'));
        setAuthenticated(true);
        loadCodes();
        loadCurrentPromo();
        loadSettings();
      } else {
        const data = await res.json();
        setLoginError(data.detail || 'Credenziali non valide');
      }
    } catch (e) { setLoginError('Errore di connessione'); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = async () => {
    localStorage.removeItem('admin_token');
    window.dispatchEvent(new Event('admin-auth-changed'));
    setAuthenticated(false);
    onClose();
  };

  const loadCurrentPromo = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/current-promo`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCurrentPromo(data.current_code || '');
        setCurrentPromo2(data.current_code_2 || '');
      }
    } catch (e) { /* ignore */ }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (e) { /* ignore */ }
  };

  // Counters of analyses unlocked with coupons / fixed codes. Nothing about visitors: the site no longer counts them.
  const loadAnalysisStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/analysis-stats`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAnalysisStats({
          couponDay: data.coupon_analyses_today, couponTotal: data.coupon_analyses_total,
          fixedDay: data.fixed_code_analyses_today, fixedTotal: data.fixed_code_analyses_total,
        });
      }
    } catch (e) { /* ignore */ }
  };

  // Refresh the analysis counters every 60s while the panel is authenticated
  useEffect(() => {
    if (!authenticated) return;
    loadAnalysisStats();
    const t = setInterval(loadAnalysisStats, 60000);
    return () => clearInterval(t);
  }, [authenticated]);

  const toggleSetting = async (key) => {
    setSettingsLoading((s) => ({ ...s, [key]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ [key]: !settings[key] }),
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (e) { /* ignore */ }
    finally { setSettingsLoading((s) => ({ ...s, [key]: false })); }
  };

  const handleUpdatePromo = async (e, slot = 1) => {
    e.preventDefault();
    const setLoading = slot === 2 ? setPromoLoading2 : setPromoLoading;
    const setMsg = slot === 2 ? setPromoMsg2 : setPromoMsg;
    const value = slot === 2 ? newPromo2 : newPromo;
    setLoading(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/update-promo-code`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ new_code: value, slot }),
      });
      const data = await res.json();
      setMsg({ ok: res.ok, text: data.message || data.detail });
      if (res.ok) {
        if (slot === 2) { setCurrentPromo2(value.trim()); setNewPromo2(''); }
        else { setCurrentPromo(value.trim()); setNewPromo(''); }
      }
    } catch (e) { setMsg({ ok: false, text: 'Errore' }); }
    finally { setLoading(false); }
  };

  const handleGenerateCodes = async () => {
    setGenLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/generate-codes`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (data.codes) {
        setCodes(prev => [...data.codes.map(c => ({ code_display: c, used: false, created_at: new Date().toISOString() })), ...prev]);
      }
      loadCodes();
    } catch (e) { console.error(e); }
    finally { setGenLoading(false); }
  };

  const loadCodes = async () => {
    setCodesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/codes`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCodes(data.codes || []);
        setCodeStats(data.stats || {});
      }
    } catch (e) { console.error(e); }
    finally { setCodesLoading(false); }
  };

  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheMsg, setCacheMsg] = useState(null);
  const [cycleRefreshLoading, setCycleRefreshLoading] = useState(false);
  const [cycleRefreshMsg, setCycleRefreshMsg] = useState(null);
  const [dailyRefreshLoading, setDailyRefreshLoading] = useState(false);
  const [dailyRefreshMsg, setDailyRefreshMsg] = useState(null);
  const handleClearCache = async (prefix = '') => {
    setCacheLoading(true);
    setCacheMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/clear-cache`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefix }),
      });
      const data = await res.json();
      if (res.ok) setCacheMsg({ ok: true, text: `Rimosse ${data.removed} entries (${data.prefix})` });
      else setCacheMsg({ ok: false, text: data.detail || 'Errore' });
    } catch (e) { setCacheMsg({ ok: false, text: 'Errore di rete' }); }
    finally { setCacheLoading(false); }
  };

  const handleForceRefreshCycle = async () => {
    setCycleRefreshLoading(true);
    setCycleRefreshMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/market/force-refresh-cycle`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok) setCycleRefreshMsg({ ok: true, text: `Cache ciclo invalidata (${data.invalidated} chiavi)` });
      else setCycleRefreshMsg({ ok: false, text: data.detail || 'Errore' });
    } catch (e) { setCycleRefreshMsg({ ok: false, text: 'Errore di rete' }); }
    finally { setCycleRefreshLoading(false); }
  };

  const handleForceRefreshDaily = async () => {
    setDailyRefreshLoading(true);
    setDailyRefreshMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/stock/force-refresh-daily`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok) setDailyRefreshMsg({ ok: true, text: `Nuovo titolo estratto: ${data.ticker} (${data.name || ''})` });
      else setDailyRefreshMsg({ ok: false, text: data.detail || 'Errore' });
    } catch (e) { setDailyRefreshMsg({ ok: false, text: 'Errore di rete' }); }
    finally { setDailyRefreshLoading(false); }
  };

  const copyCode = (code) => {
    try {
      // Fallback for non-HTTPS contexts
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-surface border border-line rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto  animate-fade-in-up" data-testid="admin-panel">
        <div className="sticky top-0 bg-surface/95 backdrop-blur-xl border-b border-line px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <Key size={18} weight="duotone" className="text-accent" />
            <h2 className="font-outfit font-bold text-lg text-text-primary">Pannello Admin</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={20} /></button>
        </div>

        <div className="p-5">
          {!authenticated ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="text-text-muted text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Username</label>
                <input data-testid="admin-username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required
                  className="w-full bg-bg border border-line rounded-lg px-3 py-2.5 text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50" />
              </div>
              <div>
                <label className="text-text-muted text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Password</label>
                <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full bg-bg border border-line rounded-lg px-3 py-2.5 text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50" />
              </div>
              {loginError && <p className="text-accent-red text-sm">{loginError}</p>}
              <button type="submit" disabled={loginLoading} data-testid="admin-login-submit"
                className="w-full py-2.5 bg-accent-deep text-accent border border-accent-deep font-outfit font-bold rounded-lg hover:bg-accent-deep-hover transition-all disabled:opacity-50">
                {loginLoading ? <SpinnerGap size={16} className="animate-spin mx-auto" /> : 'Accedi'}
              </button>
            </form>
          ) : (
            /* Admin Dashboard */
            <div className="space-y-6">
              {/* Analysis counters (coupon + fixed code) */}
              <div className="grid grid-cols-2 gap-3" data-testid="admin-analysis-stats">
                <div className="bg-bg border border-accent/15 rounded-lg px-4 py-3">
                  <p className="text-text-muted text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Analisi con coupon (oggi)</p>
                  <p className="font-mono font-bold text-2xl text-accent" data-testid="admin-coupon-analyses-daily">
                    {analysisStats.couponDay === null || analysisStats.couponDay === undefined ? '—' : analysisStats.couponDay}
                  </p>
                </div>
                <div className="bg-bg border border-accent/15 rounded-lg px-4 py-3">
                  <p className="text-text-muted text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Analisi con coupon (totale)</p>
                  <p className="font-mono font-bold text-2xl text-accent" data-testid="admin-coupon-analyses-total">
                    {analysisStats.couponTotal === null || analysisStats.couponTotal === undefined ? '—' : analysisStats.couponTotal}
                  </p>
                </div>
                <div className="bg-bg border border-accent-purple/15 rounded-lg px-4 py-3">
                  <p className="text-text-muted text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Analisi con codice fisso (oggi)</p>
                  <p className="font-mono font-bold text-2xl text-accent-purple" data-testid="admin-fixed-analyses-daily">
                    {analysisStats.fixedDay === null || analysisStats.fixedDay === undefined ? '—' : analysisStats.fixedDay}
                  </p>
                </div>
                <div className="bg-bg border border-accent-purple/15 rounded-lg px-4 py-3">
                  <p className="text-text-muted text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Analisi con codice fisso (totale)</p>
                  <p className="font-mono font-bold text-2xl text-accent-purple" data-testid="admin-fixed-analyses-total">
                    {analysisStats.fixedTotal === null || analysisStats.fixedTotal === undefined ? '—' : analysisStats.fixedTotal}
                  </p>
                </div>
              </div>

              {/* App Settings (toggles) */}
              <div data-testid="admin-app-settings">
                <div className="flex items-center gap-2 mb-3">
                  <Sliders size={16} weight="duotone" className="text-accent" />
                  <h3 className="font-outfit font-semibold text-sm text-text-primary">Impostazioni App</h3>
                </div>
                <div className="space-y-2">
                  {[
                    { key: 'analyses_enabled', label: 'Analisi Titoli', desc: 'Disabilita per manutenzione: mostra banner arancione e blocca il tasto Analizza.', icon: ChartBar, color: 'accent-orange' },
                    { key: 'promo_day_enabled', label: 'Promo Day', desc: 'Mostra il form "presentati e chiedi un coupon" nella pagina Analisi Titoli.', icon: Gift, color: 'accent-green' },
                    { key: 'stripe_enabled', label: 'Pagamenti Stripe', desc: 'Carta, Apple Pay, Google Pay', icon: CreditCard, color: 'accent' },
                    { key: 'bmc_enabled', label: 'Pagamenti Buy Me a Coffee', desc: 'Sostegno via BMC (alternativa)', icon: Coffee, color: 'accent-green' },
                  ].map(({ key, label, desc, icon: Icon, color }) => {
                    const active = settings[key];
                    const loading = settingsLoading[key];
                    return (
                      <div key={key} className="flex items-center gap-3 px-3 py-2.5 bg-bg rounded-lg border border-line">
                        <Icon size={18} weight="duotone" className={`text-${color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-outfit font-semibold text-xs text-text-primary truncate">{label}</p>
                          <p className="text-[10px] text-text-muted truncate">{desc}</p>
                        </div>
                        <button
                          onClick={() => toggleSetting(key)}
                          disabled={loading}
                          data-testid={`toggle-${key}`}
                          aria-label={`Toggle ${label}`}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50 ${
                            active ? 'bg-accent' : 'bg-white/10'
                          }`}
                        >
                          <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white  transition-transform duration-200 ${
                            active ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
                          }`} />
                          {loading && <SpinnerGap size={10} className="absolute inset-0 m-auto animate-spin text-white" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Admin credentials — managed on Render, single source of truth */}
              <div className="border-t border-line pt-5" data-testid="admin-credentials-notice">
                <div className="flex items-center gap-2 mb-3">
                  <Password size={16} weight="duotone" className="text-accent-purple" />
                  <h3 className="font-outfit font-semibold text-sm text-text-primary">Utenza Admin</h3>
                </div>
                <div className="flex items-start gap-2.5 bg-accent-purple/[0.07] border border-accent-purple/20 rounded-lg px-3 py-2.5">
                  <Warning size={15} weight="duotone" className="text-accent-purple flex-shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed">
                    <p className="text-text-primary font-medium">Utenza admin gestita su Render.</p>
                    <p className="text-text-muted mt-1">
                      Username e password si cambiano dalle variabili d&rsquo;ambiente
                      <span className="font-mono text-text-secondary"> ADMIN_USERNAME</span> e
                      <span className="font-mono text-text-secondary"> ADMIN_PASSWORD</span>, seguite da un redeploy.
                    </p>
                  </div>
                </div>
              </div>

              {/* Update Promo Code #1 */}
              <div className="border-t border-line pt-5" data-testid="admin-update-promo">
                <div className="flex items-center gap-2 mb-3">
                  <Key size={16} weight="duotone" className="text-accent-green" />
                  <h3 className="font-outfit font-semibold text-sm text-text-primary">Codice Promo Fisso #1</h3>
                </div>
                {currentPromo && (
                  <div className="mb-3 px-3 py-2 bg-bg rounded-lg border border-line flex items-center justify-between">
                    <span className="text-text-muted text-xs">Attuale:</span>
                    <span className="font-mono text-sm font-bold text-accent-green">{currentPromo}</span>
                  </div>
                )}
                <form onSubmit={(e) => handleUpdatePromo(e, 1)} className="flex gap-2">
                  <input type="text" value={newPromo} onChange={(e) => setNewPromo(e.target.value)} placeholder="Nuovo codice promo" required
                    className="flex-1 bg-bg border border-line rounded-lg px-3 py-2 text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50" />
                  <button type="submit" disabled={promoLoading}
                    className="px-4 py-2 bg-accent-green/15 text-accent-green border border-accent-green/30 rounded-lg text-sm font-medium hover:bg-accent-green/25 transition-all disabled:opacity-50">
                    {promoLoading ? '...' : 'Aggiorna'}
                  </button>
                </form>
                {promoMsg && <p className={`text-xs mt-2 flex items-center gap-1 ${promoMsg.ok ? 'text-accent-green' : 'text-accent-red'}`}>
                  {promoMsg.ok ? <CheckCircle size={12} /> : <Warning size={12} />} {promoMsg.text}</p>}
              </div>

              {/* Update Promo Code #2 */}
              <div className="border-t border-line pt-5" data-testid="admin-update-promo-2">
                <div className="flex items-center gap-2 mb-3">
                  <Key size={16} weight="duotone" className="text-accent" />
                  <h3 className="font-outfit font-semibold text-sm text-text-primary">Codice Promo Fisso #2</h3>
                </div>
                <div className="mb-3 px-3 py-2 bg-bg rounded-lg border border-line flex items-center justify-between">
                  <span className="text-text-muted text-xs">Attuale:</span>
                  <span className="font-mono text-sm font-bold text-accent">{currentPromo2 || '— (disattivo)'}</span>
                </div>
                <form onSubmit={(e) => handleUpdatePromo(e, 2)} className="flex gap-2">
                  <input type="text" value={newPromo2} onChange={(e) => setNewPromo2(e.target.value)} placeholder="Secondo codice (vuoto = disattiva)"
                    className="flex-1 bg-bg border border-line rounded-lg px-3 py-2 text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50" />
                  <button type="submit" disabled={promoLoading2}
                    className="px-4 py-2 bg-accent/15 text-accent border border-accent/30 rounded-lg text-sm font-medium hover:bg-accent/25 transition-all disabled:opacity-50">
                    {promoLoading2 ? '...' : 'Aggiorna'}
                  </button>
                </form>
                {promoMsg2 && <p className={`text-xs mt-2 flex items-center gap-1 ${promoMsg2.ok ? 'text-accent-green' : 'text-accent-red'}`}>
                  {promoMsg2.ok ? <CheckCircle size={12} /> : <Warning size={12} />} {promoMsg2.text}</p>}
              </div>

              {/* Generate Codes */}
              <div className="border-t border-line pt-5" data-testid="admin-generate-codes">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Gift size={16} weight="duotone" className="text-accent" />
                    <h3 className="font-outfit font-semibold text-sm text-text-primary">Codici Monouso</h3>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-mono">
                    <span className="text-accent-green">{codeStats.available || 0} attivi</span>
                    <span className="text-text-muted">{codeStats.used || 0} usati</span>
                  </div>
                </div>

                <button onClick={handleGenerateCodes} disabled={genLoading} data-testid="generate-codes-button"
                  className="w-full py-2.5 mb-3 bg-accent/15 text-accent border border-accent/30 rounded-lg text-sm font-bold hover:bg-accent/25 transition-all disabled:opacity-50">
                  {genLoading ? <span className="flex items-center justify-center gap-2"><SpinnerGap size={14} className="animate-spin" /> Generazione...</span> : 'Genera 20 Nuovi Codici'}
                </button>

                {/* Codes list */}
                {codesLoading ? (
                  <div className="text-center py-3"><SpinnerGap size={20} className="animate-spin text-accent mx-auto" /></div>
                ) : codes.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto space-y-1 bg-bg rounded-lg p-2 border border-line">
                    {codes.map((c, i) => (
                      <div key={i} className={`flex items-center justify-between px-2.5 py-1.5 rounded text-xs ${c.used ? 'opacity-40' : ''}`}>
                        <span className={`font-mono ${c.used ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                          {c.code_display}
                        </span>
                        <div className="flex items-center gap-2">
                          {c.used ? (
                            <span className="text-accent-red text-[10px]">Usato</span>
                          ) : (
                            <button onClick={() => copyCode(c.code_display)}
                              className="text-text-muted hover:text-accent transition-colors">
                              {copied === c.code_display ? <CheckCircle size={14} className="text-accent-green" /> : <Copy size={14} />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-muted text-xs text-center py-3">Nessun codice generato</p>
                )}
              </div>

              {/* Market cycle force refresh */}
              <div className="border-t border-line pt-5">
                <h3 className="font-outfit font-semibold text-sm text-text-primary mb-2">Ciclo di Mercato</h3>
                <p className="text-text-muted text-xs mb-3">
                  Forza il ricalcolo dell'analisi del ciclo di mercato (cache di 24h). Utile dopo aggiornamenti al modello o anomalie.
                </p>
                <button onClick={handleForceRefreshCycle} disabled={cycleRefreshLoading}
                  className="w-full py-2 bg-accent/15 text-accent border border-accent/30 rounded-lg text-xs font-bold hover:bg-accent/25 transition-all disabled:opacity-50">
                  {cycleRefreshLoading ? <><SpinnerGap size={14} className="inline animate-spin mr-1" />Aggiornamento...</> : 'Aggiorna Ciclo di Mercato'}
                </button>
                {cycleRefreshMsg && (
                  <p className={`text-xs mt-2 ${cycleRefreshMsg.ok ? 'text-accent-green' : 'text-accent-red'}`}>
                    {cycleRefreshMsg.text}
                  </p>
                )}
              </div>

              {/* Daily free analysis force refresh */}
              <div className="border-t border-line pt-5" data-testid="admin-daily-free-section">
                <h3 className="font-outfit font-semibold text-sm text-text-primary mb-2">Analisi Gratuita del Giorno</h3>
                <p className="text-text-muted text-xs mb-3">
                  Backup manuale: se la sezione resta vuota nella pagina Analisi Titoli, pesca un titolo nuovo e diverso da quello del giorno (mai lo stesso), con dati freschi. Utile quando il titolo del giorno non ha dati disponibili.
                </p>
                <button onClick={handleForceRefreshDaily} disabled={dailyRefreshLoading} data-testid="force-refresh-daily-button"
                  className="w-full py-2 bg-accent/15 text-accent border border-accent/30 rounded-lg text-xs font-bold hover:bg-accent/25 transition-all disabled:opacity-50">
                  {dailyRefreshLoading ? <><SpinnerGap size={14} className="inline animate-spin mr-1" />Ricerca in corso...</> : 'Pesca Nuova Analisi Pulita'}
                </button>
                {dailyRefreshMsg && (
                  <p className={`text-xs mt-2 ${dailyRefreshMsg.ok ? 'text-accent-green' : 'text-accent-red'}`}>
                    {dailyRefreshMsg.text}
                  </p>
                )}
              </div>

              {/* Cache management */}
              <div className="border-t border-line pt-5" data-testid="admin-cache-section">
                <h3 className="font-outfit font-semibold text-sm text-text-primary mb-2">Cache Dati Stock</h3>
                <p className="text-text-muted text-xs mb-3">
                  Forza il refresh quando una analisi mostra dati a zero (cache "avvelenata" da fallback yahoo_v8).
                </p>
                <div className="flex gap-2">
                  <button onClick={() => handleClearCache('fundamentals_')} disabled={cacheLoading}
                    data-testid="clear-fundamentals-cache"
                    className="flex-1 py-2 bg-accent-purple/15 text-accent-purple border border-accent-purple/30 rounded-lg text-xs font-bold hover:bg-accent-purple/25 transition-all disabled:opacity-50">
                    {cacheLoading ? '...' : 'Svuota cache fondamentali'}
                  </button>
                  <button onClick={() => handleClearCache('')} disabled={cacheLoading}
                    data-testid="clear-all-cache"
                    className="flex-1 py-2 bg-accent-red/15 text-accent-red border border-accent-red/30 rounded-lg text-xs font-bold hover:bg-accent-red/25 transition-all disabled:opacity-50">
                    {cacheLoading ? '...' : 'Svuota TUTTO il cache'}
                  </button>
                </div>
                {cacheMsg && (
                  <p className={`text-xs mt-2 ${cacheMsg.ok ? 'text-accent-green' : 'text-accent-red'}`} data-testid="cache-msg">
                    {cacheMsg.text}
                  </p>
                )}
              </div>

              {/* Logout */}
              <div className="border-t border-line pt-4">
                <button onClick={handleLogout} data-testid="admin-logout"
                  className="flex items-center gap-2 text-text-muted text-sm hover:text-accent-red transition-colors">
                  <SignOut size={16} /> Esci
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
